import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState, useCallback } from "react";
import agent from "../../../agent";

const initialPageConfig = {
    page: 0,
    pageSize: 10,
    totalElements: 0
};

const OrderHistory = ({ orderNum, isHistoryDialog, setIsHistoryDialog }) => {
    const { statusesProcessing } = useSelector((state) => state?.orderStatus ?? {});
    const [pageConfig, setPageConfig] = useState(initialPageConfig);
    const [loading, setLoading] = useState(false);
    const [rawHistory, setRawHistory] = useState([]);

    const close = () => isHistoryDialog && setIsHistoryDialog(false);

    useEffect(() => {
        if (!isHistoryDialog || !orderNum) return;

        fetchOrderHistory(0, pageConfig.pageSize);
    }, [isHistoryDialog, orderNum]);

    const fetchOrderHistory = useCallback(
        async (page = pageConfig.page, pageSize = pageConfig.pageSize) => {
            if (!orderNum) return;
            setLoading(true);
            try {
                const data = await agent.StatusesMonitors.getOrderHistory(orderNum, page, pageSize);
                setRawHistory(data?.content ?? []);
                setPageConfig((prev) => ({
                    ...prev,
                    page: data?.number ?? page,
                    pageSize: data?.size ?? pageSize,
                    totalElements: data?.totalElements ?? 0
                }));
            } finally {
                setLoading(false);
            }
        },
        [orderNum, pageConfig.page]
    );

    const statusMap = useMemo(() => {
        if (!statusesProcessing) {
            return;
        }

        const map = new Map();

        statusesProcessing.forEach((s) => map.set(Number(s.id), s.nameRu));

        return map;
    }, [statusesProcessing]);

    const formatDate = (value) => {
        if (!value) {
            return "";
        }

        return value.split(" ")[0];
    };

    const formatTime = (value) => {
        if (!value) {
            return "";
        }

        return value.split(" ")[1];
    };

    const formatString = (value) => {
        if (!value) {
            return "";
        }

        const stringValue = String(value);

        switch (stringValue.length) {
            case 0:
                return stringValue;
            case 1:
                return stringValue.toUpperCase();
            default:
                const [ firstLetter, ...otherLetters ] = stringValue.split("");
                return firstLetter.toUpperCase() + otherLetters.join("").toLowerCase();
        }
    }

    const tableData = useMemo(() => {
        return (rawHistory ?? []).map((item) => {
            const {
                id,
                userName,
                postalName,
                statusAdmin,
                comment,
                problemSituation,
                createdDate,
                resolvedDate
            } = item;

            const statusAdminName = statusMap.get(Number(statusAdmin))
                ?? (typeof statusAdmin === "string" ? statusAdmin : "");

            return {
                id,
                userName,
                postalName,
                statusAdmin: formatString(statusAdminName),
                comment: formatString(comment),
                problemSituation: formatString(problemSituation),
                createdDate: formatDate(createdDate),
                createdTime: formatTime(createdDate),
                resolvedDate: formatDate(resolvedDate)
            };
        });
    }, [rawHistory, statusMap]);

    const handlePageChange = async (e) => {
        await fetchOrderHistory(e.page, e.rows);
    };

    return isHistoryDialog && (
        <Dialog
            header={`История заказа ${orderNum ?? ""}`}
            visible
            onHide={close}
            style={{ width: "60rem", maxWidth: "95vw" }}
            modal
        >
            <DataTable
                value={tableData}
                className="p-datatable-customers p-datatable-sm"
                dataKey="id"
                rowHover
                emptyMessage={loading ? "Загрузка..." : "Данные не найдены"}
                loading={loading}
            >
                <Column field="userName" header="Ответственный" />
                <Column field="postalName" header="Перевозчик" style={{ width: "12rem" }} />
                <Column field="statusAdmin" header="Статус обработки" />
                <Column field="comment" header="Комментарий клиенту" />
                <Column field="problemSituation" header="Проблемные ситуации" />
                <Column field="createdDate" header="Дата" />
                <Column field="createdTime" header="Время" />
                <Column field="resolvedDate" header="Дата решения проблемы" />
            </DataTable>

            <Paginator
                currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                page={pageConfig.page}
                first={pageConfig.page * pageConfig.pageSize}
                rows={pageConfig.pageSize}
                totalRecords={pageConfig.totalElements}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onPageChange={handlePageChange}
            />
        </Dialog>
    );
};

export default OrderHistory;